import { useEffect, useRef } from 'react';
import { fetchMe } from '../lib/api/me';
import { setMyLocale } from '../lib/api/account';
import { useLocale } from './LocaleProvider';
import { bindAccountLocale, isLocale, readLocale } from './localePreference';
import type { Locale } from './catalogs';

// ═══════════════════════════════════════════════════════════════════
// THE PREFERENCE FOLLOWS THE PERSON (#310).
//
// `localePreference.ts` knows WHERE a locale is kept. This knows WHEN the
// answer changes — which is exactly twice per session, at sign-in and at
// sign-out — and it is the only module that joins the language seam to the
// session and the API.
//
// ── WHAT HAPPENS WHEN THE TWO DISAGREE ─────────────────────────────
//
// On sign-in the ACCOUNT wins. It is the more specific statement of intent:
// somebody who chose English on their own login meant it, and a browser
// default left behind on the club's desk machine is not an argument against
// that. The exception is «never chosen» — a null from the server — where
// there is no account statement to prefer and the browser's value stands.
//
// On sign-OUT the browser's value comes back, so the machine returns to
// whatever it was set to rather than keeping the language of the last person
// who signed in on it. Nothing from the account is written here, which is the
// other half of that: a shared computer does not accumulate preferences
// belonging to people who have left.
//
// ── WHO THIS IS FOR, AND WHO IT IS NOT ─────────────────────────────
//
// A club/staff account — a `User` row, reached by an `sps_` session. A MEMBER
// is a `Player` with a `MemberSession` and no `User` row at all, so there is
// nowhere to put their preference; members keep using the browser, and where
// theirs should live is an open question rather than something guessed at
// here. `/sa` authenticates the same `User` through Entra and reads the same
// `/me`, so the seam fits it unchanged — it simply has no language control
// yet to bind.
//
// ── FAILURE IS A NON-EVENT ─────────────────────────────────────────
//
// `fetchMe` never throws and reports `no-api` in a build with no API at all.
// Anything other than a recognised locale — a failed call, an older server
// with no such field, a catalog this build does not have — leaves the browser
// value in place. The worst outcome is that the language does not follow
// somebody, which is the situation this change improves on, not a regression.
// ═══════════════════════════════════════════════════════════════════

/** Seams, so a test can drive this without a server. Defaults are the real thing. */
export interface AccountLocaleDeps {
  /** Reads the signed-in account, including its stored locale. */
  loadMe: typeof fetchMe;
  /** Writes the account's locale. Resolves false when the server did not take it. */
  save: typeof setMyLocale;
  /** This browser's own stored preference, for the signed-out case. */
  browserLocale: () => Locale;
}

const DEFAULT_DEPS: AccountLocaleDeps = {
  loadMe: fetchMe,
  save: setMyLocale,
  browserLocale: readLocale,
};

/**
 * The account's stored preference, or null when it has none we can use.
 *
 * Exported because «what does the server say» is worth testing on its own,
 * away from React: the three ways it can answer nothing — not signed in, no
 * API, an unrecognised value — all have to end in the same place.
 */
export async function accountStoredLocale(
  loadMe: AccountLocaleDeps['loadMe'] = fetchMe,
): Promise<Locale | null> {
  const res = await loadMe();
  if (res.state !== 'ok') return null;
  const stored = res.me.account.locale;
  // NULL AND AN UNKNOWN STRING ARE THE SAME ANSWER HERE. Both mean «no
  // account preference this build can honour», and a locale we do not have
  // would fall back on every string — a broken-looking screen rather than a
  // stale setting.
  return isLocale(stored) ? stored : null;
}

/**
 * Bind the language preference to the signed-in account for as long as there
 * is one.
 *
 * `signedIn` is passed in rather than read from a session module so this hook
 * does not have to know WHICH door the person came through — the club portal
 * subscribes to `clubSession`, and a second surface can pass its own answer.
 *
 * ORDER MATTERS, and it is the reason this is a hook and not two calls at the
 * call site: the store is bound BEFORE the account's preference is fetched,
 * so a language chosen during that round trip is already going to the
 * account; and it is unbound on sign-out BEFORE the browser value is
 * restored, so the restore cannot be mistaken for a choice and written back.
 */
export function useAccountLocale(signedIn: boolean, deps: Partial<AccountLocaleDeps> = {}): void {
  const { loadMe, save, browserLocale } = { ...DEFAULT_DEPS, ...deps };
  const { adoptLocale, locale } = useLocale();
  // WHAT THE SCREEN IS SHOWING RIGHT NOW, readable from inside a promise that
  // was created several renders ago. The effect closes over `signedIn` only,
  // so `locale` captured there would be the value at sign-in — exactly the
  // stale value this guard exists to detect.
  const showing = useRef(locale);
  showing.current = locale;

  useEffect(() => {
    if (!signedIn) {
      // Signed out: the browser owns the preference again, and the machine
      // goes back to its own language rather than keeping the last
      // administrator's.
      bindAccountLocale(null);
      adoptLocale(browserLocale());
      return undefined;
    }

    // Bound FIRST. From this moment a choice is written to the account, even
    // if the read below is still in flight or never answers.
    bindAccountLocale({
      save: async (locale) => {
        await save(locale);
      },
    });

    // `cancelled` rather than an AbortController: `fetchMe` swallows its own
    // failures and there is nothing to abort — what has to be prevented is a
    // late answer from a session that has since ended overwriting the
    // language of the one that replaced it.
    let cancelled = false;
    // ── AND A STALE READ MUST NOT UNDO A FRESH CHOICE ────────────────
    //
    // `/me` is answered from a row read BEFORE the round trip. The picker now
    // sits in the header and on the login screen, so the likeliest moment for
    // somebody to change language is precisely while this is in flight: they
    // land on the wrong language and fix it immediately.
    //
    // Their choice is written to the account (the store was bound above), so
    // the DATA is right — but adopting the older server value would flip the
    // screen back under them, and the only way to find out it had stuck would
    // be to reload. A setting that appears to reject you is worse than one
    // that is slow.
    //
    // So the answer is dropped when the screen has moved since the read
    // started. Comparing the VALUE rather than counting choices is deliberate:
    // choosing Norwegian and then Norwegian again has changed nothing, and
    // there is nothing to protect.
    const readFrom = showing.current;
    void accountStoredLocale(loadMe).then((stored) => {
      if (cancelled || !stored) return;
      if (showing.current !== readFrom) return;
      adoptLocale(stored);
    });

    return () => {
      cancelled = true;
    };
    // `adoptLocale` and the deps are stable; `signedIn` is the only thing
    // that should re-run this, and re-running it on every render would refetch
    // `/me` in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);
}
